import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from './supabase';
import { Teacher } from './types';
import { getGoogleDriveDirectLink } from './utils/uploadUtils';
import { buildWhatsAppUrl } from './utils/phoneUtils';
import Footer from './components/Footer';
import useSeoMeta from './hooks/useSeoMeta';

import './HomePage.css'; // Re-use the animations from HomePage
import './TeacherProfilePage.css';

const GRADIENTS = [
  'linear-gradient(135deg,#4647d3 0%,#0050d4 100%)',
  'linear-gradient(135deg,#8126cf 0%,#4647d3 100%)',
  'linear-gradient(135deg,#0050d4 0%,#00c6fb 100%)',
  'linear-gradient(135deg,#f093fb 0%,#f5576c 100%)',
  'linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)',
  'linear-gradient(135deg,#fa709a 0%,#fee140 100%)',
];

const gradientFor = (name: string) =>
  GRADIENTS[name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GRADIENTS.length];

export default function TeacherProfilePage() {
  const { teacherId } = useParams();
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!teacherId) { setLoading(false); return; }
      try {
        const { data, error } = await supabase.from('teachers').select('*').eq('id', teacherId).single();
        if (error || !data) { setLoading(false); return; }
        setTeacher({
          id: data.id,
          name: data.name, subject: data.subject, district: data.district,
          bio: data.bio || `Experienced ${data.subject} teacher in ${data.district}`,
          mobile: data.mobile, whatsapp: data.whatsapp, medium: data.medium,
          specificLocation: data.specific_location, imageUrl: data.image_url,
          email: data.email, education: data.education, university: data.university,
          pdfUrls: data.pdf_urls || [],
        });
      } catch {
        // not found
      } finally {
        setLoading(false);
      }
    };
    fetchTeacher();
  }, [teacherId]);

  const teacherLocation = teacher?.district || teacher?.specificLocation || 'Sri Lanka';
  const teacherBio = teacher
    ? teacher.bio ?? `Experienced ${teacher.subject} teacher in ${teacherLocation}`
    : '';
  const teacherBioPreview = teacherBio.substring(0, 150);
  const teacherImage = teacher?.imageUrl ? getGoogleDriveDirectLink(teacher.imageUrl) : undefined;
  const teacherProfilePath = teacherId ? `/teacher/${teacherId}` : '/find-teachers';
  const seoTitle = teacher
    ? `${teacher.name} | ${teacher.subject} Teacher${teacherLocation ? ` in ${teacherLocation}` : ''} | LankaTutor`
    : loading
      ? 'Loading Teacher Profile | LankaTutor'
      : 'Teacher Not Found | LankaTutor';
  const seoDescription = teacher
    ? `${teacher.name} is a ${teacher.subject} teacher${teacherLocation ? ` based in ${teacherLocation}` : ''}. ${teacherBioPreview}${teacherBio.length > 150 ? '...' : ''}`
    : loading
      ? 'Loading teacher profile on LankaTutor.'
      : 'The requested teacher profile could not be found on LankaTutor.';
  const seoKeywords = teacher
    ? [
        teacher.name,
        teacher.subject,
        `${teacher.subject} teacher`,
        `${teacher.subject} tuition`,
        `${teacher.subject} teacher in ${teacherLocation}`,
        teacher.medium ? `${teacher.medium} medium ${teacher.subject} teacher` : '',
        'Sri Lanka teachers',
        'LankaTutor',
      ].filter(Boolean) as string[]
    : ['LankaTutor', 'Sri Lanka teachers'];
  const seoStructuredData = teacher
    ? {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: teacher.name,
        description: teacherBio,
        jobTitle: `${teacher.subject} Teacher`,
        url: teacherProfilePath,
        image: teacherImage,
        email: teacher.email,
        telephone: teacher.mobile || teacher.whatsapp,
        address: {
          '@type': 'PostalAddress',
          addressLocality: teacherLocation,
          addressCountry: 'LK',
        },
        alumniOf: teacher.university,
        knowsAbout: [teacher.subject, teacher.medium].filter(Boolean),
        worksFor: {
          '@type': 'Organization',
          name: 'LankaTutor',
        },
      }
    : undefined;

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    canonicalPath: teacherProfilePath,
    image: teacherImage,
    keywords: seoKeywords,
    robots: teacher ? 'index,follow' : loading ? 'noindex,nofollow' : 'noindex,nofollow',
    openGraphType: teacher ? 'profile' : 'website',
    structuredData: seoStructuredData,
  });

  if (loading) {
    return (
      <div style={{ ...styles.root, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.spinner} />
          <p style={{ color: '#8e9498', fontSize: '0.875rem' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div style={{ ...styles.root, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...styles.card, padding: '3rem', textAlign: 'center', maxWidth: 400 }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '1.25rem', margin: '0 auto 1rem', display: 'grid', placeItems: 'center', background: 'rgba(37, 99, 235, 0.08)', color: '#2563eb' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '2rem' }}>person_search</span>
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2c2f31', marginBottom: '0.5rem' }}>Teacher Not Found</h2>
          <p style={{ color: '#595c5e', marginBottom: '2rem' }}>This profile may have been removed or deactivated.</p>
          <Link to="/find-teachers" style={styles.solidButton}>
            Browse Teachers
          </Link>
        </div>
      </div>
    );
  }

  const hasImage = teacher.imageUrl && !imgError;
  const initial = teacher.name.charAt(0).toUpperCase();
  const bgGradient = gradientFor(teacher.name);
  const teacherFullLocation = [teacher.specificLocation, teacher.district].filter(Boolean).join(', ') || 'Sri Lanka';
  const summaryBadges = [teacher.subject, teacher.medium ? `${teacher.medium} medium` : '', teacher.district || '', teacher.education || '']
    .filter(Boolean)
    .slice(0, 4);
  const heroSummary = teacherBio.length > 170 ? `${teacherBio.substring(0, 170)}...` : teacherBio;
  const details = [
    { label: 'Location', value: teacherFullLocation, icon: 'location_on' },
    { label: 'Teaching Medium', value: teacher.medium || 'Not specified', icon: 'translate' },
    { label: 'Education', value: teacher.education || 'Not specified', icon: 'school' },
    { label: 'University', value: teacher.university || 'Not specified', icon: 'account_balance' },
  ];

  // Parse URLs safely
  const parsedPdfUrls = (typeof teacher.pdfUrls === 'string' ? JSON.parse(teacher.pdfUrls) : teacher.pdfUrls) || [];

  return (
    <div className="teacher-profile-page" style={styles.root}>
      <div className="teacher-profile-nav-shell" style={styles.navShell}>
        <div className="teacher-profile-nav-container" style={styles.navContainer}>
          <div className="teacher-profile-nav-actions" style={styles.navActions}>
            <Link to="/find-teachers" style={styles.navBackButton}>Back</Link>
            <div style={styles.navAvatarFrame}>
              {hasImage ? (
                <img
                  src={getGoogleDriveDirectLink(teacher.imageUrl!)}
                  alt={teacher.name}
                  style={styles.navAvatarImage}
                  onError={() => setImgError(true)}
                />
              ) : (
                <div style={{ ...styles.navAvatarFallback, background: bgGradient }}>{initial}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="teacher-profile-main-wrap" style={styles.mainWrap}>
        <section style={styles.heroSection}>
          <div style={styles.heroOverlay} />
          <div style={styles.heroDiagonal} />

          <div className="teacher-profile-hero-inner" style={styles.heroInner}>
            <div className="teacher-profile-hero-copy" style={styles.heroCopyColumn}>
              <span style={styles.heroPill}>{teacher.subject.toUpperCase()} TEACHER</span>
              <h1 className="teacher-profile-editorial-title" style={styles.editorialTitle}>{teacher.name}</h1>
              <p className="teacher-profile-editorial-intro" style={styles.editorialIntro}>{heroSummary}</p>

              <div className="teacher-profile-badge-row" style={styles.badgeRow}>
                {summaryBadges.map((badge) => (
                  <span key={badge} className="teacher-profile-hero-badge" style={styles.heroBadge}>{badge}</span>
                ))}
              </div>

              <div className="teacher-profile-actions" style={styles.heroActionRow}>
                {teacher.whatsapp && (
                  <a
                    href={buildWhatsAppUrl(teacher.whatsapp, 'Hi! I found you on LankaTutor and I am interested in joining your class.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.heroPrimaryButton}
                  >
                    <span className="material-symbols-outlined">chat</span>
                    WhatsApp Me
                  </a>
                )}
                {teacher.mobile && (
                  <a href={`tel:${teacher.mobile}`} style={styles.heroSecondaryButton}>
                    <span className="material-symbols-outlined">call</span>
                    Call Now
                  </a>
                )}
              </div>
            </div>

            <div className="teacher-profile-hero-media" style={styles.heroMediaColumn}>
              <div className="teacher-profile-image-card" style={styles.heroImageCard}>
                {hasImage ? (
                  <img
                    src={getGoogleDriveDirectLink(teacher.imageUrl!)}
                    alt={teacher.name}
                    style={styles.heroPortrait}
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div style={{ ...styles.heroPortraitFallback, background: bgGradient }}>{initial}</div>
                )}
              </div>

              <div className="teacher-profile-floating-card" style={styles.floatingInfoCard}>
                <div style={styles.floatingIconBox}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.35rem', color: '#006b5e' }}>school</span>
                </div>
                <div>
                  <p className="teacher-profile-floating-value" style={styles.floatingValue}>{teacher.education || teacher.medium || 'Professional Tutor'}</p>
                  <p className="teacher-profile-floating-label" style={styles.floatingLabel}>{teacher.university || teacherFullLocation}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="teacher-profile-content-section" style={styles.contentSection}>
          <div className="teacher-profile-content-grid" style={styles.contentGrid}>
            <div style={styles.mainColumn}>
              <article className="animate-fade-in-up" style={styles.contentCard}>
                <div style={styles.sectionHeadingRow}>
                  <div style={styles.headingBar} />
                  <h2 style={styles.sectionHeading}>Professional Biography</h2>
                </div>
                <div style={styles.proseBlock}>
                  <p style={styles.paragraphPrimary}>{teacherBio}</p>
                </div>
              </article>

              <article className="animate-fade-in-up delay-100" style={styles.contentCard}>
                <div style={styles.sectionHeadingRow}>
                  <div style={styles.headingBar} />
                  <h2 style={styles.sectionHeading}>Learning Resources</h2>
                </div>

                {parsedPdfUrls.length > 0 ? (
                  <div className="teacher-profile-resource-grid" style={styles.resourceGrid}>
                    {parsedPdfUrls.map((pdf: any, idx: number) => (
                      <a key={idx} href={pdf.url} target="_blank" rel="noopener noreferrer" style={styles.resourceCard}>
                        <div style={styles.resourceIconBox}>
                          <span className="material-symbols-outlined" style={{ color: '#ba1a1a' }}>picture_as_pdf</span>
                        </div>
                        <div style={styles.resourceContent}>
                          <p className="teacher-profile-resource-title" style={styles.resourceTitle}>{pdf.name}</p>
                          <p style={styles.resourceMeta}>{pdf.type || 'PDF document'}</p>
                        </div>
                        <span className="material-symbols-outlined" style={styles.resourceArrow}>download</span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyStateCard}>
                    <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: '#94a3b8' }}>folder_open</span>
                    <p style={styles.emptyStateText}>No learning resources uploaded yet.</p>
                  </div>
                )}
              </article>
            </div>

            <aside className="teacher-profile-side-column" style={styles.sideColumn}>
              <article className="animate-fade-in-up delay-100" style={styles.sidebarCard}>
                <h3 style={styles.sidebarTitle}>Quick Details</h3>
                <div style={styles.detailStack}>
                  {details.map((item) => (
                    <div key={item.label} className="teacher-profile-detail-item" style={styles.detailItem}>
                      <div style={styles.detailIconBox}>
                        <span className="material-symbols-outlined" style={{ color: '#3c4946' }}>{item.icon}</span>
                      </div>
                      <div>
                        <p style={styles.detailItemLabel}>{item.label}</p>
                        <p className="teacher-profile-detail-value" style={styles.detailItemValue}>{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              <article className="animate-fade-in-up delay-200" style={styles.sidebarPrimaryCard}>
                <h3 style={styles.sidebarPrimaryTitle}>Start Learning Today</h3>
                <p style={styles.sidebarPrimaryText}>
                  Contact the teacher directly to ask about classes, availability, and subject support.
                </p>
                <div style={styles.sidebarActionStack}>
                  {teacher.whatsapp && (
                    <a
                      href={buildWhatsAppUrl(teacher.whatsapp, 'Hi! I found you on LankaTutor and I am interested in joining your class.')}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.sidebarWhatsappButton}
                    >
                      <span className="material-symbols-outlined">chat</span>
                      WhatsApp Me
                    </a>
                  )}
                  {teacher.mobile && (
                    <a href={`tel:${teacher.mobile}`} style={styles.sidebarCallButton}>
                      <span className="material-symbols-outlined">call</span>
                      Call Now
                    </a>
                  )}
                </div>
              </article>

              <article className="animate-fade-in-up delay-300" style={styles.locationCard}>
                <div style={styles.locationBackdrop} />
                <div style={styles.locationChip}>{teacher.district || 'Sri Lanka'}</div>
                <div className="teacher-profile-location-card-body" style={styles.locationCardBody}>
                  <span className="material-symbols-outlined" style={styles.locationIcon}>location_on</span>
                  <div>
                    <p style={styles.locationLabel}>Teaching Area</p>
                    <p className="teacher-profile-location-value" style={styles.locationValue}>{teacherFullLocation}</p>
                  </div>
                </div>
              </article>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─── Inline styles ─── */
const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: "'Plus Jakarta Sans', 'Lexend', system-ui, sans-serif",
    background: 'linear-gradient(180deg, #f8fbff 0%, #f8fafc 34%, #ffffff 100%)',
    color: '#0f172a',
    minHeight: '100vh',
    overflowX: 'hidden',
  },
  navShell: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    background: 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1.5rem',
    minHeight: '64px',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  navBrandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  navLogo: {
    fontSize: '1.25rem',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    color: '#0f766e',
    textDecoration: 'none',
  },
  navMenuDesktop: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
  },
  navMenuActive: {
    color: '#0f766e',
    borderBottom: '2px solid #0d9488',
    paddingBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textDecoration: 'none',
  },
  navMenuLink: {
    color: '#475569',
    fontWeight: 500,
    fontSize: '0.875rem',
    textDecoration: 'none',
  },
  navActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  navBackButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.55rem 1rem',
    borderRadius: '999px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#2563eb',
    background: '#ffffff',
    border: '1px solid rgba(148, 163, 184, 0.24)',
    textDecoration: 'none',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
  },
  navAvatarFrame: {
    width: '40px',
    height: '40px',
    borderRadius: '999px',
    overflow: 'hidden',
    border: '2px solid rgba(37, 99, 235, 0.12)',
    background: '#ffffff',
  },
  navAvatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  navAvatarFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: 800,
  },
  mainWrap: {
    paddingTop: '64px',
  },
  heroSection: {
    position: 'relative',
    background: 'linear-gradient(135deg, #0f172a 0%, #172554 48%, #1e3a8a 100%)',
    overflow: 'hidden',
    minHeight: '500px',
    display: 'flex',
    alignItems: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.18), rgba(37, 99, 235, 0.06) 48%, transparent)',
    zIndex: 10,
  },
  heroDiagonal: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.08)',
    clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0% 100%)',
    zIndex: 0,
  },
  heroInner: {
    position: 'relative',
    zIndex: 20,
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '3rem',
    alignItems: 'center',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '5rem 1.5rem',
  },
  heroCopyColumn: {
    gridColumn: 'span 7 / span 7',
  },
  heroMediaColumn: {
    gridColumn: 'span 5 / span 5',
    position: 'relative',
  },
  heroPill: {
    display: 'inline-block',
    padding: '0.4rem 1rem',
    borderRadius: '999px',
    background: 'rgba(191, 219, 254, 0.14)',
    color: '#dbeafe',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    marginBottom: '1.5rem',
    border: '1px solid rgba(191, 219, 254, 0.18)',
  },
  editorialTitle: {
    fontSize: 'clamp(3rem, 6vw, 4.75rem)',
    lineHeight: 1,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 1.5rem',
    letterSpacing: '-0.05em',
  },
  editorialIntro: {
    fontSize: '1.25rem',
    lineHeight: 1.7,
    color: '#e1e3e4',
    maxWidth: '42rem',
    margin: '0 0 2rem',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '2rem',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.55rem 0.95rem',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.16)',
    color: '#f8f9fa',
    fontSize: '0.84rem',
    fontWeight: 600,
  },
  heroActionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  heroPrimaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '1rem 2rem',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 18px 40px rgba(37, 99, 235, 0.28)',
  },
  heroSecondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '1rem 2rem',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  },
  heroImageCard: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 5',
    borderRadius: '1.5rem',
    overflow: 'hidden',
    boxShadow: '0 30px 60px rgba(15, 23, 42, 0.24)',
    transform: 'rotate(3deg)',
    transition: 'transform 0.5s ease',
  },
  heroPortrait: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  heroPortraitFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '5rem',
    fontWeight: 900,
  },
  floatingInfoCard: {
    position: 'absolute',
    left: '-1.5rem',
    bottom: '-1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    background: '#ffffff',
    padding: '1.5rem',
    borderRadius: '1rem',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  },
  floatingIconBox: {
    width: '3rem',
    height: '3rem',
    borderRadius: '0.75rem',
    background: 'rgba(37, 99, 235, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  floatingValue: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#191c1d',
  },
  floatingLabel: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#3c4946',
    textTransform: 'uppercase',
    letterSpacing: '-0.02em',
  },
  contentSection: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '6rem 1.5rem',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '4rem',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5rem',
    gridColumn: 'span 8 / span 8',
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    gridColumn: 'span 4 / span 4',
  },
  contentCard: {
    background: 'transparent',
  },
  sectionHeadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
  },
  headingBar: {
    width: '3rem',
    height: '0.25rem',
    background: '#2563eb',
  },
  sectionHeading: {
    margin: 0,
    fontSize: '1.875rem',
    fontWeight: 800,
    letterSpacing: '-0.025em',
    color: '#191c1d',
  },
  proseBlock: {
    background: '#ffffff',
    borderRadius: '1.5rem',
    padding: '2rem',
    borderLeft: '4px solid #2563eb',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
  },
  paragraphPrimary: {
    margin: 0,
    color: '#3c4946',
    lineHeight: 1.75,
    fontSize: '1.05rem',
  },
  resourceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '1rem',
  },
  resourceCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    background: '#ffffff',
    gap: '1rem',
    borderRadius: '0.75rem',
    border: '1px solid rgba(187, 202, 197, 0.2)',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.15s ease',
  },
  resourceIconBox: {
    width: '3rem',
    height: '3rem',
    borderRadius: '0.5rem',
    background: 'rgba(186, 26, 26, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  resourceContent: {
    flex: 1,
    minWidth: 0,
  },
  resourceTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#191c1d',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resourceMeta: {
    margin: 0,
    fontSize: '0.75rem',
    color: '#3c4946',
    marginTop: '0.2rem',
  },
  resourceArrow: {
    color: '#6c7a76',
  },
  emptyStateCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '2rem',
    background: '#ffffff',
    borderRadius: '1rem',
    border: '1px solid rgba(187, 202, 197, 0.2)',
  },
  emptyStateText: {
    margin: 0,
    color: '#334155',
    fontSize: '0.95rem',
  },
  sidebarCard: {
    background: '#ffffff',
    borderRadius: '1.5rem',
    padding: '2rem',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
    border: '1px solid rgba(187, 202, 197, 0.1)',
  },
  sidebarTitle: {
    margin: '0 0 1.5rem',
    fontSize: '1.125rem',
    fontWeight: 800,
    color: '#191c1d',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  detailStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  detailIconBox: {
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '0.75rem',
    background: '#edeeef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  detailItemLabel: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#3c4946',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  detailItemValue: {
    margin: '0.2rem 0 0',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#191c1d',
  },
  sidebarPrimaryCard: {
    background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
    borderRadius: '1.5rem',
    padding: '2rem',
    color: '#ffffff',
  },
  sidebarPrimaryTitle: {
    margin: '0 0 1rem',
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  sidebarPrimaryText: {
    margin: '0 0 2rem',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    color: 'rgba(0, 32, 27, 0.8)',
  },
  sidebarActionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sidebarWhatsappButton: {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '1rem',
    background: '#ffffff',
    color: '#2563eb',
    fontWeight: 700,
    textDecoration: 'none',
  },
  sidebarCallButton: {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '1rem',
    borderRadius: '1rem',
    background: '#1e293b',
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'none',
  },
  locationCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '1.5rem',
    minHeight: '12rem',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    background: 'linear-gradient(135deg, #ffffff, #f8fbff)',
  },
  locationBackdrop: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), transparent)',
  },
  locationChip: {
    position: 'absolute',
    bottom: '1rem',
    left: '1rem',
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(8px)',
    padding: '0.35rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
  locationCardBody: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '2rem',
    height: '100%',
  },
  locationIcon: {
    fontSize: '2rem',
    color: '#2563eb',
  },
  locationLabel: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#3c4946',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  locationValue: {
    margin: '0.25rem 0 0',
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#191c1d',
  },
  card: {
    background: '#ffffff',
    borderRadius: '1.5rem',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    boxShadow: '0 10px 24px rgba(25, 28, 29, 0.06)',
  },
  solidButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.9rem 1.4rem',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'none',
  },
  spinner: {
    width: '48px', height: '48px',
    border: '4px solid #e2e8f0', borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
};
